import type { Breakpoint } from "@mui/material";
import { useMediaQuery as useMediaQuery_ } from "@react-hookz/web";
import { removeAtMedia } from "scmp-sdk";

import { useWindow } from "shared/hooks";
import { theme } from "shared/lib/styles";

export type MediaQueryOption = { initializeWithValue?: boolean };

const useMediaQuery = (breakpointString: string, options?: MediaQueryOption) =>
  useMediaQuery_(removeAtMedia(breakpointString), options);

/**
 *  Check current screen is fitting the breakpoint string or not
 *  @example useResponsive(theme.breakpoint.up("desktop"))
 */
export const useResponsive = (
  breakpointString: string,
  defaultValue = false,
  options?: MediaQueryOption,
) => {
  const mediaValue = useMediaQuery(breakpointString, { initializeWithValue: false, ...options });
  return mediaValue ?? defaultValue;
};

type BreakpointWithoutXLarge =
  | "largeDesktopUp"
  | Exclude<Breakpoint, "largeDesktop" | "xLargeDesktop">;
export const useBreakpoint = () => {
  const isLargeDesktopUp = useMediaQuery(theme.breakpoints.up("largeDesktop"), {
    initializeWithValue: false,
  });
  const isMediumDesktop = useMediaQuery(theme.breakpoints.only("mediumDesktop"), {
    initializeWithValue: false,
  });
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between("desktop", "mediumDesktop"), {
    initializeWithValue: false,
  });
  const isTablet = useMediaQuery(theme.breakpoints.only("tablet"), { initializeWithValue: false });
  const isMobile = useMediaQuery(theme.breakpoints.only("mobile"), { initializeWithValue: false });
  const window = useWindow();
  // When it's under ssr, return mobile
  if (window) return "mobile";
  const map: Record<BreakpointWithoutXLarge, boolean | undefined> = {
    desktop: isSmallDesktop,
    largeDesktopUp: isLargeDesktopUp,
    mediumDesktop: isMediumDesktop,
    mobile: isMobile,
    tablet: isTablet,
  };
  return (
    (Object.keys(map).find(
      breakpoint => map[breakpoint as BreakpointWithoutXLarge],
    ) as BreakpointWithoutXLarge) ?? "mobile"
  );
};
